import { inject, Injectable, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService, Country } from '@app/core/services/http';
import { filter, skip } from 'rxjs';

const COUNTRY_STORAGE_KEY = 'currentCountryId';

@Injectable({
  providedIn: 'root',
})
export class CountryContextService {
  private readonly userService = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _countries = signal<Country[]>([]);
  private readonly _selectedCountryId = signal<string>('');
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);
  private _initialized = false;

  readonly countries = this._countries.asReadonly();
  readonly selectedCountryId = this._selectedCountryId.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  readonly selectedCountry = computed<Country | null>(() => {
    const id = this._selectedCountryId();
    return this._countries().find(c => c.id === id) || null;
  });

  constructor() {
    this.initCountries();
    this.subscribeToUserChanges();
  }

  private subscribeToUserChanges(): void {
    this.userService.selectedUser
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        skip(1),
        filter(user => !!user)
      )
      .subscribe(() => {
        this.initCountries();
      });
  }

  private initCountries(): void {
    this._loading.set(true);
    this._error.set(null);

    const userCountries = this.userService.countries;
    const mapped: Country[] = userCountries.map(c => ({
      id: c.id,
      name: c.name,
      code: c.code,
    }));
    this._countries.set(mapped);
    this.initSelectedCountry(mapped);
    this._loading.set(false);
    this._initialized = true;
  }

  private initSelectedCountry(availableCountries: Country[]): void {
    if (!availableCountries.length) {
      this._selectedCountryId.set('');
      localStorage.removeItem(COUNTRY_STORAGE_KEY);
      return;
    }

    const savedId = localStorage.getItem(COUNTRY_STORAGE_KEY) || '';
    const userDefaultId = this.userService.countryId;
    const firstId = availableCountries[0]?.id || '';

    const exists = (id: string | null | undefined): boolean =>
      !!id && availableCountries.some(c => c.id === id);

    let finalId = '';

    if (exists(savedId)) {
      finalId = savedId;
    } else if (exists(userDefaultId)) {
      finalId = userDefaultId;
    } else {
      finalId = firstId;
    }

    this._selectedCountryId.set(finalId);
    localStorage.setItem(COUNTRY_STORAGE_KEY, finalId);
  }

  setCountry(countryId: string): void {
    if (!countryId) return;
    if (!this._countries().some(c => c.id === countryId)) return;

    this._selectedCountryId.set(countryId);
    localStorage.setItem(COUNTRY_STORAGE_KEY, countryId);
  }
}
